import React, { useState, useCallback, useMemo } from 'react';
import { getAIDetailedGovernanceReport, downloadGovernancePDF } from '../api';

const RISK_BADGE = {
  Low: { bg: '#dcfce7', color: '#166534', border: '#86efac', icon: '\u2705' },
  Medium: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d', icon: '\u26A0\uFE0F' },
  High: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', icon: '\u{1F6A8}' },
  Critical: { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4', icon: '\u{1F6D1}' },
};

const EFFORT_BADGE = {
  Low: { bg: '#dcfce7', color: '#166534' },
  Medium: { bg: '#fef3c7', color: '#92400e' },
  High: { bg: '#fee2e2', color: '#991b1b' },
};

const TAB_CONFIG = [
  { key: 'executive', label: 'Executive Overview', icon: '\u{1F4CB}' },
  { key: 'impact', label: 'Business Impact', icon: '\u{1F4CA}' },
  { key: 'pillars', label: 'Pillar Deep Dive', icon: '\u{1F50D}' },
  { key: 'roadmap', label: '90-Day Roadmap', icon: '\u{1F5FA}\uFE0F' },
  { key: 'quickwins', label: 'Quick Wins', icon: '\u26A1' },
  { key: 'target', label: 'Projected Target State', icon: '\u{1F3AF}' },
];

export default function AIGovernanceDetailedReport({ assessmentData }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('executive');
  const [cacheKey, setCacheKey] = useState(null);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const buildPayload = useMemo(() => {
    if (!assessmentData?.pillars) return null;
    const pillarEntries = Object.entries(assessmentData.pillars);
    const overallPct = assessmentData.overall_percentage || assessmentData.overall_score || 0;
    const totalItems = assessmentData.total_items || assessmentData.total_controls || 0;
    const totalPassed = assessmentData.passed || 0;

    return {
      overallMaturity: overallPct,
      totalAssessmentItems: totalItems,
      needsAttention: totalItems - totalPassed,
      pillars: pillarEntries.map(([, p]) => {
        const items = p.items || p.controls || [];
        const failed = items.filter(it => (it.maturity_score || 0) < 2).map(it => it.name || it.label || '');
        const partial = items.filter(it => {
          const s = it.maturity_score || 0;
          return s >= 2 && s < 4;
        }).map(it => it.name || it.label || '');

        return {
          name: p.name,
          score: p.maturity_score || 0,
          riskLevel: (p.risk_level || 'high').charAt(0).toUpperCase() + (p.risk_level || 'high').slice(1),
          maturityPercent: p.score || 0,
          failedControls: failed,
          partiallyCompliantControls: partial,
        };
      }),
    };
  }, [assessmentData]);

  const handleGenerate = useCallback(async () => {
    if (!buildPayload) return;
    const key = JSON.stringify(buildPayload);
    if (key === cacheKey && result) return;

    setLoading(true);
    setError(null);
    try {
      const res = await getAIDetailedGovernanceReport(buildPayload);
      setResult(res.data);
      setCacheKey(key);
      setActiveTab('executive');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to generate detailed advisory report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [buildPayload, cacheKey, result]);

  const handleRegenerate = useCallback(async () => {
    setCacheKey(null);
    if (!buildPayload) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAIDetailedGovernanceReport(buildPayload);
      setResult(res.data);
      setCacheKey(JSON.stringify(buildPayload));
      setActiveTab('executive');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to generate detailed advisory report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [buildPayload]);

  const handleDownloadPDF = useCallback(async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      const overallMaturity = buildPayload?.overallMaturity || 0;
      const res = await downloadGovernancePDF({ reportData: result, overallMaturity });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Cloud_Governance_Advisory_Report.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  }, [result, buildPayload]);

  const riskBadge = result ? (RISK_BADGE[result.overallRiskClassification] || RISK_BADGE.Medium) : null;

  const cardStyle = {
    marginBottom: '1.5rem',
    border: '1px solid #c7d2fe',
    background: 'linear-gradient(135deg, #fafbff 0%, #f0f4ff 100%)',
    borderRadius: 12,
    padding: '1.25rem 1.5rem',
    boxShadow: '0 2px 12px rgba(79,70,229,0.06)',
  };

  const tabBarStyle = {
    display: 'flex',
    gap: '0.25rem',
    borderBottom: '2px solid #e5e7eb',
    marginBottom: '1.25rem',
    overflowX: 'auto',
    paddingBottom: 0,
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: result ? '1rem' : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '1.4rem' }}>{'\u{1F9E0}'}</span>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e1b4b', fontWeight: 700 }}>AI Governance Detailed Advisory Report</h3>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {result && (
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              style={{
                padding: '7px 16px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600,
                cursor: pdfLoading ? 'not-allowed' : 'pointer',
                border: '1px solid #c7d2fe', color: '#4338ca',
                background: '#eef2ff', opacity: pdfLoading ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}
            >
              {pdfLoading ? 'Generating PDF...' : '\u{1F4E5} Download Full Advisory Report (PDF)'}
            </button>
          )}
          {result && (
            <button
              onClick={handleRegenerate}
              disabled={loading}
              style={{
                padding: '7px 16px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                border: '1px solid #c7d2fe', color: '#4338ca',
                background: '#eef2ff', opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? 'Regenerating...' : '\u{1F504} Regenerate'}
            </button>
          )}
          {!result && !loading && (
            <button
              onClick={handleGenerate}
              disabled={loading}
              style={{
                padding: '9px 24px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700,
                cursor: 'pointer', border: 'none', color: '#fff',
                background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
                boxShadow: '0 2px 10px rgba(79,70,229,0.35)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Generate AI Advisory Report
            </button>
          )}
        </div>
      </div>

      {loading && !result && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1rem', gap: '1rem' }}>
          <div style={{
            width: 44, height: 44, border: '3px solid #e0e7ff', borderTopColor: '#4f46e5',
            borderRadius: '50%', animation: 'aispin 0.8s linear infinite',
          }} />
          <style>{`@keyframes aispin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>Generating comprehensive governance advisory report...</p>
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>This may take 15-30 seconds</p>
        </div>
      )}

      {error && (
        <div style={{
          padding: '1rem', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca',
          color: '#991b1b', fontSize: '0.85rem', marginTop: '0.75rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <span>{'\u26A0\uFE0F'}</span>
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={handleGenerate} style={{
            padding: '4px 14px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
            cursor: 'pointer', border: '1px solid #fca5a5', background: '#fff', color: '#991b1b',
          }}>Retry</button>
        </div>
      )}

      {result && (
        <div>
          <div style={tabBarStyle}>
            {TAB_CONFIG.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 16px', fontSize: '0.8rem', fontWeight: 600,
                  cursor: 'pointer', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #4f46e5' : '3px solid transparent',
                  background: 'transparent', color: activeTab === tab.key ? '#4f46e5' : '#6b7280',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '0.35rem',
                }}
              >
                <span style={{ fontSize: '0.85rem' }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'executive' && <ExecutiveTab result={result} riskBadge={riskBadge} />}
          {activeTab === 'impact' && <BusinessImpactTab result={result} />}
          {activeTab === 'pillars' && <PillarDeepDiveTab result={result} expandedPillar={expandedPillar} setExpandedPillar={setExpandedPillar} />}
          {activeTab === 'roadmap' && <RoadmapTab result={result} />}
          {activeTab === 'quickwins' && <QuickWinsTab result={result} />}
          {activeTab === 'target' && <TargetStateTab result={result} />}
        </div>
      )}
    </div>
  );
}

function ExecutiveTab({ result, riskBadge }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ padding: '1.25rem', borderRadius: 10, background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', border: '1px solid #c7d2fe' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <span style={{ fontSize: '0.9rem' }}>{'\u{1F4CB}'}</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#312e81', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Executive Summary</span>
        </div>
        <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.75, margin: 0 }}>{result.executiveSummary}</p>
      </div>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{ flex: 1, padding: '1rem', borderRadius: 10, background: riskBadge.bg, border: `1px solid ${riskBadge.border}` }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: riskBadge.color, textTransform: 'uppercase', marginBottom: '0.4rem' }}>Risk Classification</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>{riskBadge.icon}</span>
            <span style={{ fontSize: '1.3rem', fontWeight: 800, color: riskBadge.color }}>{result.overallRiskClassification}</span>
          </div>
        </div>
        <div style={{ flex: 2, padding: '1rem', borderRadius: 10, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Pillar Risk Overview</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {(result.pillarAnalysis || []).map((pa, i) => {
              const eb = EFFORT_BADGE[pa.effortEstimate] || EFFORT_BADGE.Medium;
              return (
                <span key={i} style={{
                  padding: '4px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600,
                  background: eb.bg, color: eb.color, border: `1px solid ${eb.color}22`,
                }}>
                  {pa.pillarName} ({pa.effortEstimate})
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function BusinessImpactTab({ result }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ padding: '1.25rem', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <span style={{ fontSize: '0.9rem' }}>{'\u{1F4CA}'}</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Business Impact Summary</span>
        </div>
        <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.75, margin: 0 }}>{result.businessImpactSummary}</p>
      </div>

      <div style={{ padding: '1.25rem', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <span style={{ fontSize: '0.9rem' }}>{'\u{1F4D0}'}</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Maturity Gap Analysis</span>
        </div>
        <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.75, margin: 0 }}>{result.maturityGapAnalysis}</p>
      </div>
    </div>
  );
}

function PillarDeepDiveTab({ result, expandedPillar, setExpandedPillar }) {
  const pillars = result.pillarAnalysis || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {pillars.map((pillar, idx) => {
        const isExpanded = expandedPillar === idx;
        const eb = EFFORT_BADGE[pillar.effortEstimate] || EFFORT_BADGE.Medium;
        return (
          <div key={idx} style={{
            borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden',
            background: '#fff', boxShadow: isExpanded ? '0 2px 16px rgba(0,0,0,0.06)' : 'none',
            transition: 'box-shadow 0.2s',
          }}>
            <div
              onClick={() => setExpandedPillar(isExpanded ? null : idx)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '1rem 1.25rem', cursor: 'pointer', userSelect: 'none',
                background: isExpanded ? '#f8faff' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#4f46e5' }}>{idx + 1}</span>
                <span style={{ fontSize: '0.92rem', fontWeight: 600, color: '#1f2937' }}>{pillar.pillarName}</span>
                <span style={{
                  padding: '3px 10px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
                  background: eb.bg, color: eb.color,
                }}>
                  Effort: {pillar.effortEstimate}
                </span>
              </div>
              <span style={{
                fontSize: '0.9rem', color: '#9ca3af', transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>{'\u25BC'}</span>
            </div>

            {isExpanded && (
              <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid #f3f4f6' }}>
                <div style={{ padding: '0.75rem 0' }}>
                  <p style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.7, margin: '0 0 1rem 0' }}>{pillar.currentStateSummary}</p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1rem' }}>
                    {(pillar.affectedResourceCategories || []).map((res, ri) => (
                      <span key={ri} style={{
                        padding: '3px 10px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 600,
                        background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
                      }}>{res}</span>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <ImpactCard title="Security Impact" icon={'\u{1F6E1}\uFE0F'} text={pillar.securityImpact} bg="#fef2f2" border="#fecaca" titleColor="#991b1b" />
                    <ImpactCard title="Operational Impact" icon={'\u2699\uFE0F'} text={pillar.operationalImpact} bg="#fffbeb" border="#fde68a" titleColor="#92400e" />
                    <ImpactCard title="Compliance Impact" icon={'\u{1F4DC}'} text={pillar.complianceImpact} bg="#eff6ff" border="#bfdbfe" titleColor="#1e40af" />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#312e81', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Remediation Steps</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {(pillar.recommendedRemediationSteps || []).map((step, si) => (
                        <div key={si} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.84rem', color: '#374151', lineHeight: 1.6 }}>
                          <span style={{ color: '#10b981', fontWeight: 700, flexShrink: 0 }}>{si + 1}.</span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#312e81', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Recommended Cloud Services</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                      {(pillar.recommendedAzureServices || pillar.recommendedServices || []).map((svc, si) => (
                        <span key={si} style={{
                          padding: '4px 12px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600,
                          background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
                        }}>{svc}</span>
                      ))}
                    </div>
                  </div>

                  <div style={{
                    padding: '0.75rem 1rem', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca',
                  }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#991b1b' }}>Risk if Ignored: </span>
                    <span style={{ fontSize: '0.84rem', color: '#374151' }}>{pillar.riskIfIgnored}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ImpactCard({ title, icon, text, bg, border, titleColor }) {
  return (
    <div style={{ padding: '0.75rem', borderRadius: 8, background: bg, border: `1px solid ${border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '0.8rem' }}>{icon}</span>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: titleColor, textTransform: 'uppercase' }}>{title}</span>
      </div>
      <p style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.6, margin: 0 }}>{text}</p>
    </div>
  );
}

function RoadmapTab({ result }) {
  const phases = result.ninetyDayTransformationRoadmap || [];
  const phaseStyles = [
    { bg: '#eef2ff', border: '#c7d2fe', color: '#4338ca', icon: '\u{1F3C1}' },
    { bg: '#f5f3ff', border: '#ddd6fe', color: '#6d28d9', icon: '\u{1F680}' },
    { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', icon: '\u{1F3C6}' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {phases.map((phase, idx) => {
        const ps = phaseStyles[idx] || phaseStyles[0];
        return (
          <div key={idx} style={{ padding: '1.25rem', borderRadius: 10, background: ps.bg, border: `1px solid ${ps.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1.1rem' }}>{ps.icon}</span>
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: ps.color }}>{phase.phase}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {(phase.objectives || []).map((obj, oi) => (
                <div key={oi} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.86rem', color: '#374151', lineHeight: 1.65 }}>
                  <span style={{ color: ps.color, fontWeight: 700, flexShrink: 0 }}>{'\u2022'}</span>
                  <span>{obj}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuickWinsTab({ result }) {
  const wins = result.quickWins || [];
  return (
    <div>
      <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
        These are high-impact, low-effort improvements achievable within one week.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {wins.map((win, idx) => (
          <div key={idx} style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
            padding: '0.85rem 1rem', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0',
          }}>
            <span style={{
              width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', fontWeight: 800, color: '#fff', background: '#10b981', flexShrink: 0,
            }}>{idx + 1}</span>
            <span style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.65 }}>{win}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TargetStateTab({ result }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ padding: '1.25rem', borderRadius: 10, background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', border: '1px solid #bbf7d0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <span style={{ fontSize: '1rem' }}>{'\u{1F3AF}'}</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Projected Maturity After Remediation</span>
        </div>
        <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.75, margin: 0 }}>{result.projectedMaturityAfterRemediation}</p>
      </div>

      {(result.pillarAnalysis || []).length > 0 && (
        <div style={{ padding: '1rem', borderRadius: 10, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#312e81', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Pillar Remediation Effort Summary</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
            {result.pillarAnalysis.map((pa, i) => {
              const eb = EFFORT_BADGE[pa.effortEstimate] || EFFORT_BADGE.Medium;
              return (
                <div key={i} style={{
                  padding: '0.6rem 0.75rem', borderRadius: 8, background: '#fff', border: '1px solid #e5e7eb',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>{pa.pillarName}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 600,
                    background: eb.bg, color: eb.color,
                  }}>{pa.effortEstimate}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
